import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Container,
  Card,
  CardContent,
  IconButton,
  Divider,
  SelectChangeEvent,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Save,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { slotsService } from './slots.service';
import { CreateSlotDto } from './slots.dto';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { servicesService } from '../services/services.service';
import { Service } from '../services/services.dto';

const AddSlot = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const { showError } = useToast();

  // Generate time slots in 15-minute intervals
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minute.toString().padStart(2, '0');
        const timeValue = `${hourStr}:${minuteStr}`;
        slots.push({
          value: timeValue,
          label: timeValue,
        });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // State management
  const [formData, setFormData] = useState<CreateSlotDto>({
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    timeInterval: 45,
    bookingPerSlot: 1,
    serviceId: 0,
    status: 1,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch services
  useEffect(() => {
    const fetchServices = async () => {
      if (!token) return;

      try {
        setLoadingServices(true);
        servicesService.setLogoutCallback(() => navigate('/login'));
        const response = await servicesService.getServices(token, {
          page: 1,
          limit: 100,
          status: 1, // Only active services
        });
        setServices(response.data || []);
      } catch (err) {
        console.error('Error fetching services:', err);
        showError('Failed to load services');
      } finally {
        setLoadingServices(false);
      }
    };

    fetchServices();
  }, [token, navigate]);

  // Handlers
  const handleChange =
    (field: keyof CreateSlotDto) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent
    ) => {
      let value: string | number = (event.target as HTMLInputElement).value;
      if (
        field === 'serviceId' ||
        field === 'bookingPerSlot' ||
        field === 'status' ||
        field === 'timeInterval'
      ) {
        value = Number(value);
      }
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => ({
          ...prev,
          [field]: '',
        }));
      }
    };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.startDate.trim()) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate.trim()) {
      newErrors.endDate = 'End date is required';
    }

    if (!formData.startTime.trim()) {
      newErrors.startTime = 'Start time is required';
    }

    if (!formData.endTime.trim()) {
      newErrors.endTime = 'End time is required';
    }

    if (!formData.bookingPerSlot || formData.bookingPerSlot < 1) {
      newErrors.bookingPerSlot = 'Bookings per slot must be at least 1';
    }

    if (!formData.timeInterval || formData.timeInterval < 1) {
      newErrors.timeInterval = 'Time interval must be at least 1 minute';
    }

    if (!formData.serviceId || formData.serviceId === 0) {
      newErrors.serviceId = 'Service is required';
    }

    // Validate date range
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      if (endDate < startDate) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    // Validate time fields
    if (!formData.startTime) {
      newErrors.startTime = 'Start time is required';
    }

    if (!formData.endTime) {
      newErrors.endTime = 'End time is required';
    }

    // Validate that end time is after start time
    if (formData.startTime && formData.endTime) {
      const startTime = new Date(`2000-01-01T${formData.startTime}:00`);
      const endTime = new Date(`2000-01-01T${formData.endTime}:00`);
      if (endTime <= startTime) {
        newErrors.endTime = 'End time must be after start time';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!token || !isAuthenticated) {
      setError('You must be logged in to create a slot');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await slotsService.createSlot(token, formData);

      // Navigating away immediately, so the toast is shown on the Slots
      // list page it lands on (via location.state) rather than here, where
      // it would be unmounted before it could render.
      navigate('/slots', {
        state: { message: 'Slot created successfully', severity: 'success' },
      });
    } catch (err) {
      console.error('Error creating slot:', err);
      const message =
        err instanceof Error ? err.message : 'Failed to create slot';
      setError(message);
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/slots');
  };

  // Loading state
  if (authLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Render
  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <IconButton
              onClick={handleCancel}
              sx={{ mr: 2 }}
              disabled={loading}
            >
              <ArrowBack />
            </IconButton>
            <Add color="primary" sx={{ mr: 1 }} />
            <Typography variant="h4" component="h1">
              Add New Slot
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Create a new time slot for appointments with service selection
          </Typography>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Left Column - Slot Details */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Slot Details
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={2}>
                    {/* Start Date */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Start Date"
                        type="date"
                        value={formData.startDate}
                        onChange={handleChange('startDate')}
                        error={!!errors.startDate}
                        helperText={errors.startDate}
                        disabled={loading}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth
                        required
                      />
                    </Grid>

                    {/* End Date */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="End Date"
                        type="date"
                        value={formData.endDate}
                        onChange={handleChange('endDate')}
                        error={!!errors.endDate}
                        helperText={errors.endDate}
                        disabled={loading}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth
                        required
                      />
                    </Grid>

                    {/* Start Time */}
                    <Grid item xs={12} sm={6}>
                      <FormControl
                        fullWidth
                        required
                        error={!!errors.startTime}
                      >
                        <InputLabel>Start Time</InputLabel>
                        <Select
                          value={formData.startTime}
                          label="Start Time"
                          onChange={handleChange('startTime')}
                          disabled={loading}
                        >
                          <MenuItem value="">
                            <em>Select start time</em>
                          </MenuItem>
                          {timeSlots.map(slot => (
                            <MenuItem key={slot.value} value={slot.value}>
                              {slot.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.startTime && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ ml: 2, mt: 0.5 }}
                          >
                            {errors.startTime}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    {/* End Time */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required error={!!errors.endTime}>
                        <InputLabel>End Time</InputLabel>
                        <Select
                          value={formData.endTime}
                          label="End Time"
                          onChange={handleChange('endTime')}
                          disabled={loading}
                        >
                          <MenuItem value="">
                            <em>Select end time</em>
                          </MenuItem>
                          {timeSlots.map(slot => (
                            <MenuItem key={slot.value} value={slot.value}>
                              {slot.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.endTime && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ ml: 2, mt: 0.5 }}
                          >
                            {errors.endTime}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Right Column - Settings */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Slot Settings
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {/* Service Dropdown */}
                  <FormControl
                    fullWidth
                    required
                    error={!!errors.serviceId}
                    sx={{ mb: 2 }}
                  >
                    <InputLabel>Service</InputLabel>
                    <Select
                      value={
                        formData.serviceId ? String(formData.serviceId) : ''
                      }
                      label="Service"
                      onChange={handleChange('serviceId')}
                      disabled={loading || loadingServices}
                    >
                      <MenuItem value="">
                        <em>Select a service</em>
                      </MenuItem>
                      {services.map(service => (
                        <MenuItem key={service.id} value={service.id}>
                          {service.title}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.serviceId && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ ml: 2, mt: 0.5 }}
                      >
                        {errors.serviceId}
                      </Typography>
                    )}
                  </FormControl>

                  {/* Time Interval */}
                  <TextField
                    label="Time Interval (minutes)"
                    type="number"
                    value={formData.timeInterval}
                    onChange={handleChange('timeInterval')}
                    error={!!errors.timeInterval}
                    helperText={
                      errors.timeInterval || 'Duration of each appointment slot'
                    }
                    disabled={loading}
                    inputProps={{ min: 1 }}
                    fullWidth
                    required
                    sx={{ mb: 2 }}
                  />

                  {/* Booking Per Slot */}
                  {/* <TextField
                    label="Bookings Per Slot"
                    type="number"
                    value={formData.bookingPerSlot}
                    onChange={handleChange('bookingPerSlot')}
                    error={!!errors.bookingPerSlot}
                    helperText={
                      errors.bookingPerSlot || 'Max bookings per time slot'
                    }
                    disabled={loading}
                    inputProps={{ min: 1 }}
                    fullWidth
                    required
                  /> */}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box
            sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
              startIcon={<CancelIcon />}
              size="large"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              size="large"
            >
              {loading ? 'Creating...' : 'Create Slot'}
            </Button>
          </Box>
        </form>
      </Box>
    </Container>
  );
};

export default AddSlot;
